import { getButtonSized } from '@tamagui/get-button-sized';
import { IconProps } from '@tamagui/helpers-icon';
import { Plus } from '@tamagui/lucide-icons';
import { PressableVariants } from '@unistocks/components/basic/variants';
import { PRESSABLE_OPACITY } from '@unistocks/shared/consts';
import { ComponentType, PropsWithChildren } from 'react';
import {
  ColorTokens,
  createStyledContext,
  FontSizeTokens,
  getFontSize,
  getIcon,
  GetProps,
  GetRef,
  getTokenValue,
  isWeb,
  SizeTokens,
  Spinner,
  styled,
  Text,
  themeableVariants,
  Token,
  useCurrentColor,
  usePropsAndStyle,
  View,
  withStaticProperties
} from 'tamagui';

const FadeLeft = {
  o: 0,
  x: -25
};

const FadeRight = {
  o: 0,
  x: 25
};

// TODO: Local copy of tamagui's `themeableVariants.chromeless` with literal color types.
// The upstream object widens its colors to `string`, which Tamagui 2.2 rejects in
// `styled` variants, collapsing the whole Button variant prop inference.
const chromelessStyle = {
  backgroundColor: 'transparent',
  borderColor: 'transparent',
  shadowColor: 'transparent',

  hoverStyle: {
    borderColor: 'transparent'
  }
} as const;

const chromelessVariant = {
  true: chromelessStyle,
  all: {
    ...chromelessStyle,
    hoverStyle: chromelessStyle,
    pressStyle: chromelessStyle,
    focusStyle: chromelessStyle
  }
} as const;

type ButtonVariant = 'outlined' | 'link' | 'fab';

const BUTTON_NAME = 'Button';
const BUTTON_TEXT = 'ButtonText';
const BUTTON_SPINNER = 'ButtonSpinner';

const ButtonContext = createStyledContext<{
  size?: SizeTokens;
  variant?: ButtonVariant;
  color?: ColorTokens | string;
  chromeless?: boolean;
}>({
  size: undefined,
  variant: undefined,
  color: undefined,
  chromeless: undefined
});

const ButtonFrame = styled(View, {
  context: ButtonContext,
  name: BUTTON_NAME,
  render: <button type="button" />,
  role: 'button',
  tabIndex: 0,
  group: 'button',
  containerType: 'normal',

  variants: {
    unstyled: {
      false: {
        flexDirection: 'row',
        gap: '$2',
        justifyContent: 'center',
        alignItems: 'center',
        flexWrap: 'nowrap',
        backgroundColor: '$background',
        borderColor: '$borderColor',
        borderWidth: 1,
        cursor: 'pointer',

        pressStyle: {
          backgroundColor: '$backgroundPress',
          borderColor: '$borderColorPress'
        },

        hoverStyle: {
          backgroundColor: '$backgroundHover',
          borderColor: '$borderColorHover'
        },

        /* focusStyle: {
          backgroundColor: '$backgroundFocus',
          borderColor: '$borderColorFocus'
        }, */

        focusVisibleStyle: {
          outlineColor: '$outlineColor',
          outlineStyle: 'solid',
          outlineWidth: 2
        }
      }
    },

    withFeedback: PressableVariants.withFeedback,
    withScale: PressableVariants.withScale,
    circular: themeableVariants.circular,
    elevate: themeableVariants.elevate,
    chromeless: chromelessVariant,

    loading: {
      true: {
        pointerEvents: 'none',
        cursor: 'default'
      }
    },

    disabled: {
      true: {
        pointerEvents: 'none',
        // @ts-ignore
        'aria-disabled': true
      }
    },

    variant: {
      outlined: {
        backgroundColor: 'transparent',

        pressStyle: {
          backgroundColor: '$backgroundPress',
          borderColor: '$borderColorPress'
        },

        hoverStyle: {
          backgroundColor: '$backgroundHover',
          borderColor: '$borderColorHover'
        }
      },

      link: {
        backgroundColor: 'transparent',
        borderWidth: 0,
        borderColor: 'transparent',
        pl: 0,

        hoverStyle: {
          backgroundColor: 'transparent',
          borderColor: 'transparent'
        },

        pressStyle: {
          opacity: PRESSABLE_OPACITY,
          backgroundColor: 'transparent',
          borderColor: 'transparent'
        }
      },

      fab: {
        circular: true,
        position: 'absolute',
        bottom: '$3',
        right: '$3',
        elevation: '$0.25',
        zIndex: '$4',

        '$platform-web': {
          // TODO: Tamagui's WebOnlyValidStyleValues narrows `position` to `'-webkit-sticky'`,
          // which breaks the whole variant inference. Cast until upstream is fixed.
          position: 'fixed' as any
        }
      }
    },

    size: {
      '...size': (val, extras) => {
        const buttonStyle = getButtonSized(val, extras);
        const gap = getTokenValue(val as Token);
        return {
          ...buttonStyle,
          gap
        };
      },
      ':number': (val, extras) => {
        const buttonStyle = getButtonSized(val, extras);
        const gap = val * 0.4;
        return {
          ...buttonStyle,
          gap
        };
      }
    }
  } as const,

  defaultVariants: {
    unstyled: process.env.TAMAGUI_HEADLESS === '1',
    size: isWeb ? '$4' : '$5',
    withFeedback: true,
    withScale: true,
    disabled: false
  }
});

// styleable needed to receive theme when used with asChild
// const ButtonFrame = ButtonFrameImpl.styleable(props => <ButtonFrameImpl {...props} />);

type ButtonFrameProps = GetProps<typeof ButtonFrame>;

const ButtonText = styled(Text, {
  name: BUTTON_TEXT,
  context: ButtonContext,

  variants: {
    unstyled: {
      false: {
        fontFamily: '$body',
        userSelect: 'none',
        pointerEvents: 'none',
        // flexGrow 1 leads to inconsistent native style where text pushes to start of view
        flexGrow: 0,
        flexShrink: 1,
        numberOfLines: 1,
        ellipsis: true,
        color: '$color',
        fontWeight: '$7'
      }
    },

    chromeless: {
      true: {
        color: '$bodyFont',

        '$group-button-hover': {
          color: '$color'
        },

        '$group-button-press': {
          color: '$color'
        }
      }
    },

    variant: {
      outlined: {
        color: '$bodyFont',

        '$group-button-hover': {
          color: '$color'
        },

        '$group-button-press': {
          color: '$color'
        }
      },

      link: {
        color: '$bodyFont',
        textDecorationStyle: 'solid',
        textDecorationLine: 'underline'
      }
    },

    size: {
      '...fontSize': (token, { font }) => ({
        fontSize: font?.size[token]
      })
    }
  } as const,

  defaultVariants: {
    unstyled: process.env.TAMAGUI_HEADLESS === '1'
  }
});

// Icon component is redefined in ButtonIcon, so it can be any random one here
const ButtonIconImpl = styled(Plus, {
  name: BUTTON_TEXT,
  context: ButtonContext,

  variants: {
    unstyled: {
      false: {
        color: '$color'
      }
    },

    variant: {
      outlined: {
        color: '$bodyFont',

        '$group-button-hover': {
          color: '$color'
        },

        '$group-button-press': {
          color: '$color'
        }
      }
    }
  } as const,

  defaultVariants: {
    unstyled: process.env.TAMAGUI_HEADLESS === '1'
  }
});

type ButtonIconProps = PropsWithChildren<{
  scaleIcon?: number;
  size?: SizeTokens;
}>;

const ButtonIcon = ButtonIconImpl.styleable<ButtonIconProps>(
  ({ size, scaleIcon = 1.4, children, ...propsIn }, ref) => {
    const context = ButtonContext.useStyledContext();

    const sizeToken = size ?? context.size;

    const iconSize =
      (typeof sizeToken === 'number' ? sizeToken * 0.5 : getFontSize(sizeToken as FontSizeTokens)) *
      scaleIcon;

    const [props, style] = usePropsAndStyle(
      { ...context, ...propsIn, size: iconSize },
      {
        forComponent: ButtonIconImpl
      }
    );

    const colorOverride = useCurrentColor(
      context.variant === 'outlined' ? '$bodyFont' : props.color
    );

    return getIcon(children, {
      ref,
      ...props,
      style,
      color: colorOverride
    } as any);
  }
);

const ButtonSpinner = styled(Spinner, {
  name: BUTTON_SPINNER,
  size: 'small',
  color: '$color',
  scale: 0.8
});

type ButtonHTMLProps = {
  type?: 'submit' | 'reset' | 'button';
  form?: string;
  formAction?: string;
  formEncType?: string;
  formMethod?: string;
  formNoValidate?: boolean;
  formTarget?: string;
  name?: string;
  value?: string | readonly string[] | number;
};

export type ButtonProps = ButtonFrameProps &
  ButtonHTMLProps & {
    Icon?: ComponentType<IconProps>;
    IconAfter?: ComponentType<IconProps>;
    scaleIcon?: number;
    iconSize?: SizeTokens;
    loading?: boolean;
  };

export type ButtonRef = GetRef<typeof ButtonFrame>;

const ButtonComponent = ButtonFrame.styleable<ButtonProps>(
  ({ Icon, IconAfter, scaleIcon, iconSize, loading, children, ...propsIn }, ref) => {
    return (
      <ButtonFrame ref={ref} transition={'medium'} {...propsIn}>
        {Icon && !loading ? (
          <View key={'icon'} x={0} enterStyle={FadeLeft} exitStyle={FadeLeft}>
            <ButtonIcon size={iconSize} scaleIcon={scaleIcon}>
              <Icon />
            </ButtonIcon>
          </View>
        ) : null}

        {loading ? (
          <ButtonSpinner key={'spinner'} x={0} enterStyle={FadeLeft} exitStyle={FadeLeft} />
        ) : null}

        {typeof children === 'string' ? <ButtonText key={'text'}>{children}</ButtonText> : children}

        {IconAfter && !loading ? (
          <View key={'iconAfter'} x={0} enterStyle={FadeRight} exitStyle={FadeRight}>
            <ButtonIcon size={iconSize} scaleIcon={scaleIcon}>
              <IconAfter />
            </ButtonIcon>
          </View>
        ) : null}
      </ButtonFrame>
    );
  }
);

export const Button = withStaticProperties(ButtonComponent, {
  Apply: ButtonContext.Provider,
  Text: ButtonText,
  Icon: ButtonIcon,
  Spinner: ButtonSpinner
});
